import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useEffect, useMemo } from 'react'
import { getOrCreateMachineID, tryParseJSON } from '../helpers'
import {
  getTokensToDisplay,
  resyncDisplayState,
  setCurrentDisplayToken,
  setMachineID,
} from '../state/token'
import { deleteAuthInfo } from '../state/auth'
import { addNotification } from '../state/notifications'
import { Websocket } from '../components'

// Resolve the machine id SYNCHRONOUSLY at module load, before any render. This
// is the Bug 1 root-cause fix: the previous post-render useEffect generated the
// id only after the first render, so until redux-persist asynchronously
// rehydrated the `easeon_token` slice the selector returned `undefined`. That
// transient-falsy window toggled the `null` gate below (unmount -> remount),
// and each remount opened a fresh socket while the previous one was still
// server-registered, which REPLACE_STALE killed — the ~1 Hz reconnect storm.
// Reading/seeding a single durable localStorage value removes the race: the id
// is stable from the very first render, independent of rehydration timing.
const RESOLVED_MACHINE_ID = getOrCreateMachineID()

export const SocketDisplay = (props) => {
  const dispatch = useDispatch()
  const { user } = useSelector((state) => state.auth)
  const { websocketURLPrefix } = useSelector((state) => state.application)
  const { organization, machine_id: persistedMachineID } = useSelector(
    (state) => state.machineConfig
  )

  // Prefer the synchronously-resolved id so `machine_id` is never transiently
  // falsy; fall back to the persisted redux value only if localStorage was
  // unavailable (RESOLVED_MACHINE_ID === null).
  const machine_id = RESOLVED_MACHINE_ID || persistedMachineID

  // Keep the redux store in sync so any other consumer sees the same id. This
  // runs once and does NOT gate the socket, so it can no longer cause a
  // remount: the socket already renders off the stable `machine_id` above.
  useEffect(() => {
    if (machine_id && machine_id !== persistedMachineID) {
      dispatch(setMachineID(machine_id))
    }
  }, [machine_id, persistedMachineID, dispatch])

  const handleSocketData = useCallback(
    (data) => {
      const dataObject = tryParseJSON(data)
      if (dataObject && dataObject.command === 'reload') {
        dispatch(getTokensToDisplay(organization))
      }
      if (dataObject && dataObject.command === 'call') {
        dispatch(setCurrentDisplayToken(dataObject.data))
      }
      if (dataObject && dataObject.command === 'logout') {
        dispatch(deleteAuthInfo())
      }
      if (dataObject && dataObject.command === 'flash') {
        if (user) {
          dispatch(addNotification(dataObject.data))
        }
      }
    },
    [dispatch, organization, user]
  )

  // Resync authoritative display state whenever the socket (re)opens. WebSocket
  // frames are not replayed, so any `call`/`reload` the backend POSTed while the
  // socket was briefly down is lost forever. resyncDisplayState refetches the
  // token list (self-heals a missed `reload`) and, from that same authoritative
  // payload, restores the current "now serving" token (self-heals a missed
  // `call`). Kept stable via useCallback so passing it as a prop never changes
  // identity and cannot reintroduce the remount/reconnect storm (Bug 1).
  const handleSocketOpen = useCallback(() => {
    if (organization) {
      dispatch(resyncDisplayState(organization))
    }
  }, [dispatch, organization])

  // Build the socket URL only from the values that actually address the
  // connection, and keep its identity stable across unrelated re-renders so a
  // changing prop can never churn the connection (Bug 1 — reconnect storm).
  const machineLocationCode = organization && organization.token_machine_location_code
  const url = useMemo(() => {
    if (!machineLocationCode || !machine_id) {
      return null
    }
    return `${websocketURLPrefix}${machineLocationCode}/${machine_id}/${machine_id}`
  }, [websocketURLPrefix, machineLocationCode, machine_id])

  // Only open the socket once BOTH the organization and the machine id are
  // available. Opening with an undefined machine_id produced URLs like
  // ".../undefined/undefined" and churned connections.
  if (!organization || !machine_id || !url) {
    return null
  }

  return (
    <Websocket
      url={url}
      onMessage={handleSocketData}
      onOpen={handleSocketOpen}
      reconnect={true}
      debug={false}
    />
  )
}
